import {InfiniteData} from '@tanstack/query-core';
import {
  infiniteQueryOptions,
  queryOptions,
  UseInfiniteQueryOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import {LANDING_QUERY_KEY_MAP} from '../api/landing/constant';
import {getLanding} from '../api/landing/getLanding';
import {getLandingList} from '../api/landing/getLandingList';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {requestContext} from '../common/requestContext';
import {ItemSeriesDto} from '../common/shared/api/gateway/ItemSeriesDto.interface';
import {ItemSetDto} from '../common/shared/api/gateway/ItemSetDto.interface';
import {ItemSingleDto} from '../common/shared/api/gateway/ItemSingleDto.interface';
import {StaticLandingDayOfWeekParam} from '../common/shared/api/karina/params/StaticLandingDayOfWeekParam.interface';
import {StaticLandingEventParam} from '../common/shared/api/karina/params/StaticLandingEventParam.interface';
import {StaticLandingFreeSeriesParam} from '../common/shared/api/karina/params/StaticLandingFreeSeriesParam.interface';
import {StaticLandingGenreParam} from '../common/shared/api/karina/params/StaticLandingGenreParam.interface';
import {StaticLandingOriginalParam} from '../common/shared/api/karina/params/StaticLandingOriginalParam.interface';
import {StaticLandingRankingParam} from '../common/shared/api/karina/params/StaticLandingRankingParam.interface';
import {StaticLandingTodayNewParam} from '../common/shared/api/karina/params/StaticLandingTodayNewParam.interface';
import {StaticLandingTodayUpInvenParam} from '../common/shared/api/karina/params/StaticLandingTodayUpInvenParam.interface';
import {StaticLandingTodayUpParam} from '../common/shared/api/karina/params/StaticLandingTodayUpParam.interface';
import {DayOfWeekStaticLandingPageVo} from '../common/shared/api/karina/vo/page/DayOfWeekStaticLandingPageVo.interface';
import {EventStaticLandingPageVo} from '../common/shared/api/karina/vo/page/EventStaticLandingPageVo.interface';
import {FreeSeriesStaticLandingPageVo} from '../common/shared/api/karina/vo/page/FreeSeriesStaticLandingPageVo.interface';
import {LandingPageVo} from '../common/shared/api/karina/vo/page/LandingPageVo.interface';
import {GenreStaticLandingPageVo} from '../common/shared/api/nicolas/vo/page/GenreStaticLandingPageVo.interface';
import {OriginalStaticLandingPageVo} from '../common/shared/api/nicolas/vo/page/OriginalStaticLandingPageVo.interface';
import {RankingStaticLandingPageVo} from '../common/shared/api/nicolas/vo/page/RankingStaticLandingPageVo.interface';
import {RankingThemeStaticLandingPageVo} from '../common/shared/api/nicolas/vo/page/RankingThemeStaticLandingPageVo.interface';
import {TodayNewStaticLandingPageVo} from '../common/shared/api/nicolas/vo/page/TodayNewStaticLandingPageVo.interface';
import {TodayUpInvenStaticLandingPageVo} from '../common/shared/api/nicolas/vo/page/TodayUpInvenStaticLandingPageVo.interface';
import {TodayUpStaticLandingPageVo} from '../common/shared/api/nicolas/vo/page/TodayUpStaticLandingPageVo.interface';
import {trimUndefinedFromObject} from '../common/util/trimUndefinedFromObject';
import {PagewebError} from '../errors/PagewebError.class';
import {createSimpleApiResponseError} from '../graphql/api/createSimpleApiResponseError';
import {createLayout} from '../graphql/factory/Layout';
import {createSeriesCardViewLandingList} from '../graphql/factory/SeriesCardViewLandingList';
import {createSeriesListViewLandingList} from '../graphql/factory/SeriesListViewLandingList';
import {createSeriesPosterViewLandingList} from '../graphql/factory/SeriesPosterViewLandingList';
import {createSetListViewLandingList} from '../graphql/factory/SetListViewLandingList';
import {createSetPosterViewLandingList} from '../graphql/factory/SetPosterViewLandingList';
import {createSingleListViewLandingList} from '../graphql/factory/SingleListViewLandingList';
import {createStaticLandingDayOfWeekSection} from '../graphql/factory/StaticLandingDayOfWeekSection';
import {createStaticLandingEventSection} from '../graphql/factory/StaticLandingEventSection';
import {createStaticLandingFreeSeriesSection} from '../graphql/factory/StaticLandingFreeSeriesSection';
import {createStaticLandingGenreSection} from '../graphql/factory/StaticLandingGenreSection';
import {createStaticLandingOriginalSection} from '../graphql/factory/StaticLandingOriginalSection';
import {createStaticLandingRankingSection} from '../graphql/factory/StaticLandingRankingSection';
import {createStaticLandingRankingThemeSection} from '../graphql/factory/StaticLandingRankingThemeSection';
import {createStaticLandingTodayNewSection} from '../graphql/factory/StaticLandingTodayNewSection';
import {createStaticLandingTodayUpSection} from '../graphql/factory/StaticLandingTodayUpSection';
import {
  LandingType,
  Layout,
  SeriesCardViewLandingListQuery,
  SeriesCardViewLandingListQueryVariables,
  SeriesListViewLandingListQuery,
  SeriesListViewLandingListQueryVariables,
  SeriesPosterViewLandingListQuery,
  SeriesPosterViewLandingListQueryVariables,
  SetListViewLandingListQuery,
  SetListViewLandingListQueryVariables,
  SetPosterViewLandingListQuery,
  SetPosterViewLandingListQueryVariables,
  SingleListViewLandingListQuery,
  SingleListViewLandingListQueryVariables,
  StaticLandingDayOfWeekLayoutQueryVariables,
  StaticLandingDayOfWeekSectionQuery,
  StaticLandingDayOfWeekSectionQueryVariables,
  StaticLandingEventLayoutQueryVariables,
  StaticLandingEventSectionQuery,
  StaticLandingEventSectionQueryVariables,
  StaticLandingFreeSeriesLayoutQueryVariables,
  StaticLandingFreeSeriesSectionQuery,
  StaticLandingFreeSeriesSectionQueryVariables,
  StaticLandingGenreLayoutQueryVariables,
  StaticLandingGenreSectionQuery,
  StaticLandingGenreSectionQueryVariables,
  StaticLandingOriginalLayoutQueryVariables,
  StaticLandingOriginalSectionQuery,
  StaticLandingOriginalSectionQueryVariables,
  StaticLandingRankingLayoutQueryVariables,
  StaticLandingRankingSectionQuery,
  StaticLandingRankingSectionQueryVariables,
  StaticLandingRankingThemeLayoutQueryVariables,
  StaticLandingRankingThemeSectionQuery,
  StaticLandingRankingThemeSectionQueryVariables,
  StaticLandingTodayNewLayoutQueryVariables,
  StaticLandingTodayNewSectionQuery,
  StaticLandingTodayNewSectionQueryVariables,
  StaticLandingTodayUpLayoutQueryVariables,
  StaticLandingTodayUpSectionQuery,
  StaticLandingTodayUpSectionQueryVariables,
  StaticLandingType,
} from '../graphql/generated/types';

export const landingFactory = {
  /**
   * 시리즈 카드뷰 랜딩 리스트 (Infinite)
   *
   * Origin: seriesCardViewLandingList.resolver.ts
   */
  infiniteSeriesCardViewLandingList: (
    variables: SeriesCardViewLandingListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        SeriesCardViewLandingListQuery,
        PagewebError,
        InfiniteData<
          SeriesCardViewLandingListQuery,
          SeriesCardViewLandingListQueryVariables
        >,
        SeriesCardViewLandingListQuery,
        ReturnType<typeof LANDING_QUERY_KEY_MAP.seriesCardViewLandingList>,
        SeriesCardViewLandingListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.seriesCardViewLandingList(variables),
      queryFn: async ({pageParam}) => {
        // Resolver 로직 복제
        const landingType = LandingType.SeriesCard;
        const page = pageParam.page ?? 0;
        const id = `series-card-landing-${pageParam.dataKey}-theme-keyword-${
          pageParam.themeKeywordUid || 'all'
        }-page-${page}`;

        const response = await getLandingList(
          {
            landingType,
            dataKey: pageParam.dataKey,
            page,
            size: pageParam.size,
            themeKeywordUid: pageParam.themeKeywordUid,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError('결과를 받아오지 못했습니다');
        }

        const userInfo = requestContext.requestInfo?.user ?? null;
        // ⚠️ Resolver와 동일하게 factory 적용 + GraphQL 응답 형태로 래핑
        return {
          seriesCardViewLandingList: createSeriesCardViewLandingList(
            userInfo,
            response.result as LandingPageVo<ItemSeriesDto>,
            id,
            page || 0
          ),
        };
      },
      ...options,
    }),

  /**
   * 시리즈 리스트뷰 랜딩 리스트 (Infinite)
   *
   * Origin: seriesListViewLandingList.resolver.ts
   */
  infiniteSeriesListViewLandingList: (
    variables: SeriesListViewLandingListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        SeriesListViewLandingListQuery,
        PagewebError,
        InfiniteData<
          SeriesListViewLandingListQuery,
          SeriesListViewLandingListQueryVariables
        >,
        SeriesListViewLandingListQuery,
        ReturnType<typeof LANDING_QUERY_KEY_MAP.seriesListViewLandingList>,
        SeriesListViewLandingListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.seriesListViewLandingList(variables),
      queryFn: async ({pageParam}) => {
        const {dataKey, page, size, themeKeywordUid} = pageParam;

        // Resolver 로직 복제
        const landingType = LandingType.SeriesList;

        const id = `series-list-landing-${dataKey}-theme-keyword-${
          themeKeywordUid || 'all'
        }-page-${page}`;

        const response = await getLandingList(
          {
            landingType,
            dataKey,
            page,
            size,
            themeKeywordUid,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError('결과를 받아오지 못했습니다');
        }
        const userInfo = requestContext.requestInfo?.user ?? null;

        // ⚠️ Resolver와 동일하게 factory 적용 + GraphQL 응답 형태로 래핑
        return {
          seriesListViewLandingList: createSeriesListViewLandingList(
            userInfo,
            response.result as LandingPageVo<ItemSeriesDto>,
            id,
            page || 0
          ),
        };
      },
      ...options,
    }),

  /**
   * 시리즈 포스터뷰 랜딩 리스트 (Infinite)
   *
   * Origin: seriesPosterViewLandingList.resolver.ts (동일 패턴 추정)
   */
  infiniteSeriesPosterViewLandingList: (
    variables: SeriesPosterViewLandingListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        SeriesPosterViewLandingListQuery,
        PagewebError,
        InfiniteData<
          SeriesPosterViewLandingListQuery,
          SeriesPosterViewLandingListQueryVariables
        >,
        SeriesPosterViewLandingListQuery,
        ReturnType<typeof LANDING_QUERY_KEY_MAP.seriesPosterViewLandingList>,
        SeriesPosterViewLandingListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.seriesPosterViewLandingList(variables),
      queryFn: async ({pageParam}) => {
        const {dataKey, themeKeywordUid, page, size} = pageParam;
        const landingType = LandingType.SeriesPoster;

        const id = `series-poster-landing-${dataKey}-theme-keyword-${
          themeKeywordUid || 'all'
        }-page-${page}`;

        const response = await getLandingList(
          {
            landingType,
            dataKey: dataKey,
            page,
            size: size,
            themeKeywordUid: themeKeywordUid,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError('결과를 받아오지 못했습니다');
        }
        const userInfo = requestContext.requestInfo?.user ?? null;
        // ⚠️ Resolver와 동일하게 factory 적용 + GraphQL 응답 형태로 래핑
        return {
          seriesPosterViewLandingList: createSeriesPosterViewLandingList(
            userInfo,
            response.result as LandingPageVo<ItemSeriesDto>,
            id,
            page || 0
          ),
        };
      },
      ...options,
    }),

  /**
   * 세트 리스트뷰 랜딩 리스트 (Infinite)
   *
   * Origin: setListViewLandingList.resolver.ts (동일 패턴 추정)
   */
  infiniteSetListViewLandingList: (
    variables: SetListViewLandingListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        SetListViewLandingListQuery,
        PagewebError,
        InfiniteData<
          SetListViewLandingListQuery,
          SetListViewLandingListQueryVariables
        >,
        SetListViewLandingListQuery,
        ReturnType<typeof LANDING_QUERY_KEY_MAP.setListViewLandingList>,
        SetListViewLandingListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.setListViewLandingList(variables),
      queryFn: async ({pageParam}) => {
        const {dataKey, size, page} = pageParam;
        const landingType = LandingType.SetList;

        const id = `set-list-landing-${dataKey}-page-${page}`;

        const response = await getLandingList(
          {
            landingType,
            dataKey: dataKey,
            page,
            size: size,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            '세트상품 랜딩페이지 데이터를 받아오지 못했습니다'
          );
        }
        const userInfo = requestContext.requestInfo?.user ?? null;

        // ⚠️ Resolver와 동일하게 factory 적용 + GraphQL 응답 형태로 래핑
        return {
          setListViewLandingList: createSetListViewLandingList(
            userInfo,
            response.result as LandingPageVo<ItemSetDto>,
            id,
            page || 0
          ),
        };
      },
      ...options,
    }),

  /**
   * 세트 포스터뷰 랜딩 리스트 (Infinite)
   *
   * Origin: setPosterViewLandingList.resolver.ts (동일 패턴 추정)
   */
  infiniteSetPosterViewLandingList: (
    variables: SetPosterViewLandingListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        SetPosterViewLandingListQuery,
        PagewebError,
        InfiniteData<
          SetPosterViewLandingListQuery,
          SetPosterViewLandingListQueryVariables
        >,
        SetPosterViewLandingListQuery,
        ReturnType<typeof LANDING_QUERY_KEY_MAP.setPosterViewLandingList>,
        SetPosterViewLandingListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.setPosterViewLandingList(variables),
      queryFn: async ({pageParam}) => {
        const {size, page, dataKey} = pageParam;
        const landingType = LandingType.SetPoster;

        const id = `set-poster-landing-${dataKey}-page-${page}`;

        const response = await getLandingList(
          {
            landingType,
            dataKey: dataKey,
            page,
            size: size,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            '세트 랜딩페이지 결과를 받아오지 못했습니다'
          );
        }
        const userInfo = requestContext.requestInfo?.user ?? null;

        // ⚠️ Resolver와 동일하게 factory 적용 + GraphQL 응답 형태로 래핑
        return {
          setPosterViewLandingList: createSetPosterViewLandingList(
            userInfo,
            response.result as LandingPageVo<ItemSetDto>,
            id,
            page || 0
          ),
        };
      },
      ...options,
    }),

  /**
   * 단편 리스트뷰 랜딩 리스트 (Infinite)
   *
   * Origin: singleListViewLandingList.resolver.ts (동일 패턴 추정)
   */
  infiniteSingleListViewLandingList: (
    variables: SingleListViewLandingListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        SingleListViewLandingListQuery,
        PagewebError,
        InfiniteData<
          SingleListViewLandingListQuery,
          SingleListViewLandingListQueryVariables
        >,
        SingleListViewLandingListQuery,
        ReturnType<typeof LANDING_QUERY_KEY_MAP.singleListViewLandingList>,
        SingleListViewLandingListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.singleListViewLandingList(variables),
      queryFn: async ({pageParam}) => {
        const {dataKey, page, size, themeKeywordUid} = pageParam;
        const landingType = LandingType.SingleList;
        const id = `single-list-landing-${dataKey}-theme-keyword-${
          themeKeywordUid || 'all'
        }-page-${page}`;

        const response = await getLandingList(
          {
            landingType,
            dataKey: dataKey,
            page,
            size: size,
            themeKeywordUid: themeKeywordUid,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError('결과를 받아오지 못했습니다');
        }

        const userInfo = requestContext.requestInfo?.user ?? null;
        // ⚠️ Resolver와 동일하게 factory 적용 + GraphQL 응답 형태로 래핑
        return {
          singleListViewLandingList: createSingleListViewLandingList(
            userInfo,
            response.result as LandingPageVo<ItemSingleDto>,
            id,
            page || 0
          ),
        };
      },
      ...options,
    }),

  /**
   * 요일연재 고정랜딩 섹션 (Infinite)
   *
   * Origin: staticLandingDayOfWeekSection.resolver.ts
   */
  infiniteStaticLandingDayOfWeekSection: (
    variables: StaticLandingDayOfWeekSectionQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        StaticLandingDayOfWeekSectionQuery,
        PagewebError,
        InfiniteData<
          StaticLandingDayOfWeekSectionQuery,
          StaticLandingDayOfWeekSectionQueryVariables
        >,
        StaticLandingDayOfWeekSectionQuery,
        ReturnType<typeof LANDING_QUERY_KEY_MAP.staticLandingDayOfWeekSection>,
        StaticLandingDayOfWeekSectionQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.staticLandingDayOfWeekSection(variables),
      queryFn: async ({pageParam}) => {
        // Resolver 로직 복제
        // pageParam이 {param: {..., page: number}} 형태로 들어옴

        const {
          page,
          categoryUid,
          bmType,
          subcategoryUid,
          dayTabUid,
          screenUid,
        } = pageParam.param;

        const queryParam: StaticLandingDayOfWeekParam = trimUndefinedFromObject(
          {
            category_uid: categoryUid,
            page: page || 0,
            ...{
              ...(bmType && {
                bm: bmType,
              }),
            },
            ...(subcategoryUid && {
              subcategory_uid: Number(subcategoryUid),
            }),
            ...(dayTabUid && {tab_uid: Number(dayTabUid)}),
            ...(typeof screenUid === 'number' && {screen_uid: screenUid}),
          }
        ) as StaticLandingDayOfWeekParam;

        const response = await getLanding<
          DayOfWeekStaticLandingPageVo,
          StaticLandingDayOfWeekParam
        >(StaticLandingType.DayOfWeek, queryParam);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '요일연재 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        const userInfo = requestContext.requestInfo?.user ?? null;
        // ⚠️ Resolver와 동일하게 factory 적용
        // input에 현재 페이지 번호를 반영하여 param.page가 올바르게 설정되도록 함
        const inputWithPage = {...pageParam.param, page};
        return {
          staticLandingDayOfWeekSection: createStaticLandingDayOfWeekSection(
            userInfo,
            {
              sectionId: pageParam.sectionId,
              input: inputWithPage,
              dayOfWeekStaticLandingPageVo: response.result,
            }
          ),
        };
      },
      ...options,
    }),

  /**
   * 이벤트 고정랜딩 섹션 (Infinite)
   *
   * Origin: staticLandingEventSection.resolver.ts
   */
  infiniteStaticLandingEventSection: (
    variables: StaticLandingEventSectionQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        StaticLandingEventSectionQuery,
        PagewebError,
        InfiniteData<
          StaticLandingEventSectionQuery,
          StaticLandingEventSectionQueryVariables
        >,
        StaticLandingEventSectionQuery,
        ReturnType<typeof LANDING_QUERY_KEY_MAP.staticLandingEventSection>,
        StaticLandingEventSectionQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.staticLandingEventSection(variables),
      queryFn: async ({pageParam}) => {
        // Resolver 로직 복제
        const {page, categoryUid} = pageParam.param;

        const queryParam: StaticLandingEventParam = {
          category_uid: categoryUid,
          page,
        };

        const response = await getLanding<
          EventStaticLandingPageVo,
          StaticLandingEventParam
        >(StaticLandingType.Event, queryParam);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '이벤트 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        return {
          staticLandingEventSection: createStaticLandingEventSection(null, {
            sectionId: pageParam.sectionId,
            input: pageParam.param,
            eventStaticLandingPageVo: response.result,
          }),
        };
      },
      ...options,
    }),
  /**
   * 무료 시리즈 고정 랜딩 섹션 조회 (Infinite)
   *
   * ⚠️ options는 required (컴포넌트에서 initialPageParam, getNextPageParam 주입 필요)
   * ⚠️ queryFn에서 factory 적용 → Resolver와 동일하게 각 페이지마다 변환
   */
  infiniteStaticLandingFreeSeriesSection: (
    variables: StaticLandingFreeSeriesSectionQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        StaticLandingFreeSeriesSectionQuery,
        PagewebError,
        InfiniteData<
          StaticLandingFreeSeriesSectionQuery,
          StaticLandingFreeSeriesSectionQueryVariables
        >,
        StaticLandingFreeSeriesSectionQuery,
        ReturnType<
          typeof LANDING_QUERY_KEY_MAP.infiniteStaticLandingFreeSeriesSection
        >,
        StaticLandingFreeSeriesSectionQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey:
        LANDING_QUERY_KEY_MAP.infiniteStaticLandingFreeSeriesSection(variables),
      queryFn: async ({pageParam}) => {
        const {sectionId, param} = pageParam;
        // pageParam이 {param: {..., page: number}} 형태로 들어옴
        const page = pageParam?.param?.page ?? 0;
        const {categoryUid, tabUid, screenUid} = param;

        const queryParam: StaticLandingFreeSeriesParam =
          trimUndefinedFromObject({
            category_uid: categoryUid,
            tab_uid: tabUid ? Number(tabUid) : undefined,
            screen_uid: typeof screenUid === 'number' ? screenUid : undefined,
            page,
          }) as StaticLandingFreeSeriesParam;

        const response = await getLanding<
          FreeSeriesStaticLandingPageVo,
          StaticLandingFreeSeriesParam
        >(StaticLandingType.FreeSeries, queryParam);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '무료시리즈 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        // ⚠️ Resolver와 동일하게 factory 적용
        // input에 현재 페이지 번호를 반영하여 param.page가 올바르게 설정되도록 함
        const inputWithPage = {...param, page};
        const userInfo = requestContext.requestInfo?.user ?? null;
        return {
          staticLandingFreeSeriesSection: createStaticLandingFreeSeriesSection(
            userInfo,
            {
              sectionId,
              freeSeriesStaticLandingPageVo: response.result,
              input: inputWithPage,
            }
          ),
        };
      },
      ...options, // initialPageParam, getNextPageParam 포함
    }),

  /**
   * 장르 고정 랜딩 섹션 조회 (Infinite)
   *
   * ⚠️ options는 required (컴포넌트에서 initialPageParam, getNextPageParam 주입 필요)
   * ⚠️ queryFn에서 factory 적용 → Resolver와 동일하게 각 페이지마다 변환
   */
  infiniteStaticLandingGenreSection: (
    variables: StaticLandingGenreSectionQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        StaticLandingGenreSectionQuery,
        PagewebError,
        InfiniteData<
          StaticLandingGenreSectionQuery,
          StaticLandingGenreSectionQueryVariables
        >,
        StaticLandingGenreSectionQuery,
        ReturnType<
          typeof LANDING_QUERY_KEY_MAP.infiniteStaticLandingGenreSection
        >,
        StaticLandingGenreSectionQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey:
        LANDING_QUERY_KEY_MAP.infiniteStaticLandingGenreSection(variables),
      queryFn: async ({pageParam}) => {
        const {sectionId, param} = pageParam;
        // pageParam이 {param: {..., page: number}} 형태로 들어옴
        const page = pageParam?.param?.page ?? 0;
        const {categoryUid, subcategoryUid, sortType, isComplete, screenUid} =
          param;

        const queryParam: StaticLandingGenreParam = trimUndefinedFromObject({
          category_uid: categoryUid,
          subcategory_uid: subcategoryUid ? Number(subcategoryUid) : undefined,
          sort_type: sortType,
          is_complete: typeof isComplete === 'boolean' ? isComplete : undefined,
          screen_uid: typeof screenUid === 'number' ? screenUid : undefined,
          page,
        }) as StaticLandingGenreParam;

        const response = await getLanding<
          GenreStaticLandingPageVo,
          StaticLandingGenreParam
        >(StaticLandingType.Genre, queryParam);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '장르전체 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }
        const userInfo = requestContext.requestInfo?.user ?? null;

        // ⚠️ Resolver와 동일하게 factory 적용
        // input에 현재 페이지 번호를 반영하여 param.page가 올바르게 설정되도록 함
        const inputWithPage = {...param, page};
        return {
          staticLandingGenreSection: createStaticLandingGenreSection(userInfo, {
            sectionId,
            genreStaticLandingPageVo: response.result,
            input: inputWithPage,
          }),
        };
      },
      ...options, // initialPageParam, getNextPageParam 포함
    }),

  /**
   * 오리지널 고정 랜딩 섹션 조회 (Infinite)
   *
   * ⚠️ options는 required (컴포넌트에서 initialPageParam, getNextPageParam 주입 필요)
   * ⚠️ queryFn에서 factory 적용 → Resolver와 동일하게 각 페이지마다 변환
   */
  infiniteStaticLandingOriginalSection: (
    variables: StaticLandingOriginalSectionQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        StaticLandingOriginalSectionQuery,
        PagewebError,
        InfiniteData<
          StaticLandingOriginalSectionQuery,
          StaticLandingOriginalSectionQueryVariables
        >,
        StaticLandingOriginalSectionQuery,
        ReturnType<
          typeof LANDING_QUERY_KEY_MAP.infiniteStaticLandingOriginalSection
        >,
        StaticLandingOriginalSectionQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey:
        LANDING_QUERY_KEY_MAP.infiniteStaticLandingOriginalSection(variables),
      queryFn: async ({pageParam}) => {
        const {sectionId, param} = pageParam;
        // pageParam이 {param: {..., page: number}} 형태로 들어옴
        const page = pageParam?.param?.page ?? 0;
        const {categoryUid, subcategoryUid, sortType, isComplete, screenUid} =
          param;

        const queryParam: StaticLandingOriginalParam = trimUndefinedFromObject({
          category_uid: categoryUid,
          subcategory_uid: subcategoryUid ? Number(subcategoryUid) : undefined,
          sort_type: sortType,
          is_complete: typeof isComplete === 'boolean' ? isComplete : undefined,
          screen_uid: typeof screenUid === 'number' ? screenUid : undefined,
          page,
        }) as StaticLandingOriginalParam;

        const response = await getLanding<
          OriginalStaticLandingPageVo,
          StaticLandingOriginalParam
        >(StaticLandingType.Original, queryParam);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '오리지널 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        // ⚠️ Resolver와 동일하게 factory 적용
        // input에 현재 페이지 번호를 반영하여 param.page가 올바르게 설정되도록 함
        const userInfo = requestContext.requestInfo?.user ?? null;
        const inputWithPage = {...param, page};
        return {
          staticLandingOriginalSection: createStaticLandingOriginalSection(
            userInfo,
            {
              sectionId,
              originalStaticLandingPageVo: response.result,
              input: inputWithPage,
            }
          ),
        };
      },
      ...options, // initialPageParam, getNextPageParam 포함
    }),

  /**
   * 랭킹 고정 랜딩 섹션 조회 (Infinite)
   *
   * ⚠️ options는 required (컴포넌트에서 initialPageParam, getNextPageParam 주입 필요)
   * ⚠️ queryFn에서 factory 적용 → Resolver와 동일하게 각 페이지마다 변환
   * ⚠️ withOperationArea 계산 로직 포함 (section_top_v2 또는 section_card_banner 존재 여부)
   */
  infiniteStaticLandingRankingSection: (
    variables: StaticLandingRankingSectionQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        StaticLandingRankingSectionQuery,
        PagewebError,
        InfiniteData<
          StaticLandingRankingSectionQuery,
          StaticLandingRankingSectionQueryVariables
        >,
        StaticLandingRankingSectionQuery,
        ReturnType<
          typeof LANDING_QUERY_KEY_MAP.infiniteStaticLandingRankingSection
        >,
        StaticLandingRankingSectionQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey:
        LANDING_QUERY_KEY_MAP.infiniteStaticLandingRankingSection(variables),
      queryFn: async ({pageParam}) => {
        const {sectionId, param} = pageParam;
        // pageParam이 {param: {..., page: number}} 형태로 들어옴
        const page = pageParam?.param?.page ?? 0;
        const {categoryUid, subcategoryUid, rankingType, screenUid} = param;

        const queryParam: StaticLandingRankingParam = trimUndefinedFromObject({
          category_uid: categoryUid,
          subcategory_uid: subcategoryUid ? Number(subcategoryUid) : undefined,
          ranking_type: rankingType,
          screen_uid: typeof screenUid === 'number' ? screenUid : undefined,
          page,
        }) as StaticLandingRankingParam;

        const response = await getLanding<
          RankingStaticLandingPageVo,
          StaticLandingRankingParam
        >(StaticLandingType.Ranking, queryParam);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '랭킹 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        // ⚠️ Resolver와 동일하게 factory 적용
        // withOperationArea 계산: section_top_v2 또는 section_card_banner 존재 여부
        const result = response.result;
        // input에 현재 페이지 번호를 반영하여 param.page가 올바르게 설정되도록 함
        const inputWithPage = {...param, page};
        const userInfo = requestContext.requestInfo?.user ?? null;

        return {
          staticLandingRankingSection: createStaticLandingRankingSection(
            userInfo,
            {
              sectionId,
              input: inputWithPage,
              rankingStaticLandingPageVo: result,
              type: param.type,
              withOperationArea:
                (result.section_top_v2 && result.section_top_v2.length > 0) ||
                (result.section_card_banner &&
                  result.section_card_banner.length > 0),
            }
          ),
        };
      },
      ...options, // initialPageParam, getNextPageParam 포함
    }),

  /**
   * 단행본 랭킹 테마 고정 랜딩 섹션 조회 (Infinite)
   *
   * API: GET /api/gateway/view/v1/landing/ranking_theme
   * tx_id 페이징: pageParam.param.txId 를 그대로 요청에 실어 직전 응답의 tx_id 를 체이닝한다.
   */
  infiniteStaticLandingRankingThemeSection: (
    variables: StaticLandingRankingThemeSectionQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        StaticLandingRankingThemeSectionQuery,
        PagewebError,
        InfiniteData<
          StaticLandingRankingThemeSectionQuery,
          StaticLandingRankingThemeSectionQueryVariables
        >,
        StaticLandingRankingThemeSectionQuery,
        ReturnType<
          typeof LANDING_QUERY_KEY_MAP.infiniteStaticLandingRankingThemeSection
        >,
        StaticLandingRankingThemeSectionQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey:
        LANDING_QUERY_KEY_MAP.infiniteStaticLandingRankingThemeSection(
          variables
        ),
      queryFn: async ({pageParam}) => {
        const {sectionId, param} = pageParam;
        const page = pageParam?.param?.page ?? 0;
        const {screenUid, filterType, sortType, txId} = param;

        const response = await getLanding<
          RankingThemeStaticLandingPageVo,
          {
            screen_uid?: number;
            filter_type?: string;
            sort_type?: string;
            page?: number;
            tx_id?: string;
          }
        >(
          StaticLandingType.RankingTheme,
          trimUndefinedFromObject({
            screen_uid: typeof screenUid === 'number' ? screenUid : undefined,
            filter_type: filterType,
            sort_type: sortType,
            page,
            tx_id: txId,
          }),
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            '단행본 랭킹 테마 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        const result = response.result;
        const inputWithPage = {...param, page};
        const userInfo = requestContext.requestInfo?.user ?? null;

        return {
          staticLandingRankingThemeSection:
            createStaticLandingRankingThemeSection(userInfo, {
              sectionId,
              input: inputWithPage,
              rankingThemeStaticLandingPageVo: result,
              type: param.type,
            }),
        };
      },
      ...options,
    }),

  /**
   * 오늘UP 고정 랜딩 섹션 조회 (Infinite)
   *
   * ⚠️ options는 required (컴포넌트에서 initialPageParam, getNextPageParam 주입 필요)
   * ⚠️ queryFn에서 factory 적용 → Resolver와 동일하게 각 페이지마다 변환
   * ⚠️ 복잡한 로직: 두 개의 API 호출 (TodayUp + TodayUpInven 조건부)
   */
  infiniteStaticLandingTodayUpSection: (
    variables: StaticLandingTodayUpSectionQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        StaticLandingTodayUpSectionQuery,
        PagewebError,
        InfiniteData<
          StaticLandingTodayUpSectionQuery,
          StaticLandingTodayUpSectionQueryVariables
        >,
        StaticLandingTodayUpSectionQuery,
        ReturnType<
          typeof LANDING_QUERY_KEY_MAP.infiniteStaticLandingTodayUpSection
        >,
        StaticLandingTodayUpSectionQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey:
        LANDING_QUERY_KEY_MAP.infiniteStaticLandingTodayUpSection(variables),
      queryFn: async ({pageParam}) => {
        const {sectionId, param} = pageParam;

        // pageParam이 {param: {..., page: number}} 형태로 들어옴
        const page = pageParam?.param?.page ?? 0;
        const {categoryUid, subcategoryUid} = param;

        const queryParam: StaticLandingTodayUpParam = trimUndefinedFromObject({
          category_uid: categoryUid,
          subcategory_uid: subcategoryUid ? Number(subcategoryUid) : undefined,
          page,
        }) as StaticLandingTodayUpParam;

        // 1. 기본 TodayUp API 호출 (필수)
        const response = await getLanding<
          TodayUpStaticLandingPageVo,
          StaticLandingTodayUpParam
        >(StaticLandingType.TodayUp, queryParam);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '오늘UP 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        // 2. TodayUpInven API 조건부 호출
        // 조건: '전체' 서브탭 & 첫 페이지인 경우만
        let invenResult;
        if (
          (!subcategoryUid || subcategoryUid === '0') &&
          (page === 0 || page === undefined)
        ) {
          try {
            const invenQueryParam: StaticLandingTodayUpInvenParam =
              trimUndefinedFromObject({
                category_uid: categoryUid,
                subcategory_uid: subcategoryUid
                  ? Number(subcategoryUid)
                  : undefined,
              }) as StaticLandingTodayUpInvenParam;

            const invenResponse = await getLanding<
              TodayUpInvenStaticLandingPageVo,
              StaticLandingTodayUpInvenParam
            >(StaticLandingType.TodayUpInven, invenQueryParam);

            if (!invenResponse.result) {
              throw createSimpleApiResponseError(
                '오늘UP 고정랜딩 Inven 데이터를 찾을 수 없습니다.'
              );
            }
            invenResult = invenResponse.result;
          } catch (error) {
            invenResult = undefined;
          }
        }

        // ⚠️ Resolver와 동일하게 factory 적용
        // input에 현재 페이지 번호를 반영하여 param.page가 올바르게 설정되도록 함
        const inputWithPage = {...param, page};
        const userInfo = requestContext.requestInfo?.user ?? null;

        return {
          staticLandingTodayUpSection: createStaticLandingTodayUpSection(
            userInfo,
            {
              sectionId,
              todayUpStaticLandingPageVo: response.result,
              todayUpInvenStaticLandingPageVo: invenResult,
              input: inputWithPage,
            }
          ),
        };
      },
      ...options, // initialPageParam, getNextPageParam 포함
    }),

  /**
   * 오늘NEW 고정 랜딩 섹션 조회 (Infinite)
   *
   * ⚠️ options는 required (컴포넌트에서 initialPageParam, getNextPageParam 주입 필요)
   * ⚠️ queryFn에서 factory 적용 → Resolver와 동일하게 각 페이지마다 변환
   * ⚠️ TodayNew는 page 파라미터가 없으므로 단일 페이지만 지원
   */
  infiniteStaticLandingTodayNewSection: (
    variables: StaticLandingTodayNewSectionQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        StaticLandingTodayNewSectionQuery,
        PagewebError,
        InfiniteData<
          StaticLandingTodayNewSectionQuery,
          StaticLandingTodayNewSectionQueryVariables
        >,
        StaticLandingTodayNewSectionQuery,
        ReturnType<
          typeof LANDING_QUERY_KEY_MAP.infiniteStaticLandingTodayNewSection
        >,
        StaticLandingTodayNewSectionQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey:
        LANDING_QUERY_KEY_MAP.infiniteStaticLandingTodayNewSection(variables),
      queryFn: async ({pageParam}) => {
        const {sectionId, param} = pageParam;
        const {categoryUid, subcategoryUid} = param;

        const queryParam: StaticLandingTodayNewParam = trimUndefinedFromObject({
          category_uid: categoryUid,
          subcategory_uid: subcategoryUid ? Number(subcategoryUid) : undefined,
        }) as StaticLandingTodayNewParam;

        const response = await getLanding<
          TodayNewStaticLandingPageVo,
          StaticLandingTodayNewParam
        >(StaticLandingType.TodayNew, queryParam, fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '오늘NEW 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        return {
          staticLandingTodayNewSection: createStaticLandingTodayNewSection(
            null,
            {
              sectionId,
              todayNewStaticLandingPageVo: response.result,
              input: param,
            }
          ),
        };
      },
      ...options,
    }),

  /**
   * 요일연재 고정랜딩 레이아웃 조회
   *
   * Origin: staticLandingDayOfWeekLayout.resolver.ts
   * ⚠️ userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
   */
  staticLandingDayOfWeekLayout: (
    variables?: StaticLandingDayOfWeekLayoutQueryVariables,
    options?: Omit<
      UseQueryOptions<Layout | null, PagewebError, Layout | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.staticLandingDayOfWeekLayout(variables),
      queryFn: async (): Promise<Layout | null> => {
        if (!variables?.queryInput) {
          return null;
        }
        const {
          categoryUid,
          bmType,
          subcategoryUid,
          dayTabUid,
          page,
          screenUid,
        } = variables.queryInput;

        const queryParam: StaticLandingDayOfWeekParam = trimUndefinedFromObject(
          {
            category_uid: Number(categoryUid),
            page: page ?? 0,
            ...(bmType && {bm: bmType}),
            ...(subcategoryUid && {subcategory_uid: Number(subcategoryUid)}),
            ...(dayTabUid && {tab_uid: Number(dayTabUid)}),
            ...(typeof screenUid === 'number' && {screen_uid: screenUid}),
          }
        ) as StaticLandingDayOfWeekParam;

        const response = await getLanding<
          DayOfWeekStaticLandingPageVo,
          StaticLandingDayOfWeekParam
        >(StaticLandingType.DayOfWeek, queryParam, fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '요일연재 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        return createLayout({
          userInfo: null,
          param: {
            dayOfWeekStaticLandingPageVo: response.result,
            input: variables.queryInput,
            type: variables.queryInput.type,
          },
        });
      },
      ...options,
    }),

  /**
   * 이벤트 고정랜딩 레이아웃 조회
   *
   * Origin: staticLandingEventLayout.resolver.ts
   * ⚠️ userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
   */
  staticLandingEventLayout: (
    variables?: StaticLandingEventLayoutQueryVariables,
    options?: Omit<
      UseQueryOptions<Layout | null, PagewebError, Layout | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.staticLandingEventLayout(variables),
      queryFn: async (): Promise<Layout | null> => {
        if (!variables?.queryInput) {
          return null;
        }
        const {categoryUid, page} = variables.queryInput;

        const queryParam: StaticLandingEventParam = {
          category_uid: categoryUid,
          page,
        };

        const response = await getLanding<
          EventStaticLandingPageVo,
          StaticLandingEventParam
        >(StaticLandingType.Event, queryParam, fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '이벤트 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        return createLayout({
          userInfo: null,
          param: {
            eventStaticLandingPageVo: response.result,
            input: variables.queryInput,
          },
        });
      },
      ...options,
    }),

  /**
   * 무료시리즈 고정랜딩 레이아웃 조회
   *
   * Origin: staticLandingFreeSeriesLayout.resolver.ts
   * ⚠️ userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
   */
  staticLandingFreeSeriesLayout: (
    variables?: StaticLandingFreeSeriesLayoutQueryVariables,
    options?: Omit<
      UseQueryOptions<Layout | null, PagewebError, Layout | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.staticLandingFreeSeriesLayout(variables),
      queryFn: async (): Promise<Layout | null> => {
        if (!variables?.queryInput) {
          return null;
        }
        const {categoryUid, tabUid, page, screenUid} = variables.queryInput;

        const queryParam: StaticLandingFreeSeriesParam =
          trimUndefinedFromObject({
            category_uid: categoryUid,
            ...(tabUid && {tab_uid: Number(tabUid)}),
            ...(screenUid !== undefined && {screen_uid: screenUid}),
            page,
          }) as StaticLandingFreeSeriesParam;

        const response = await getLanding<
          FreeSeriesStaticLandingPageVo,
          StaticLandingFreeSeriesParam
        >(StaticLandingType.FreeSeries, queryParam, fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '무료시리즈 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        return createLayout({
          userInfo: null,
          param: {
            freeSeriesStaticLandingPageVo: response.result,
            input: variables.queryInput,
            type: variables.queryInput.type,
          },
        });
      },
      ...options,
    }),

  /**
   * 장르 고정랜딩 레이아웃 조회
   *
   * Origin: staticLandingGenreLayout.resolver.ts
   * ⚠️ userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
   */
  staticLandingGenreLayout: (
    variables?: StaticLandingGenreLayoutQueryVariables,
    options?: Omit<
      UseQueryOptions<Layout | null, PagewebError, Layout | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.staticLandingGenreLayout(variables),
      queryFn: async (): Promise<Layout | null> => {
        if (!variables?.queryInput) {
          return null;
        }
        const {
          categoryUid,
          subcategoryUid,
          sortType,
          page,
          isComplete,
          screenUid,
        } = variables.queryInput;

        const queryParam: StaticLandingGenreParam = trimUndefinedFromObject({
          category_uid: categoryUid,
          ...(subcategoryUid && {subcategory_uid: Number(subcategoryUid)}),
          ...(sortType && {sort_type: sortType}),
          ...(typeof isComplete === 'boolean' && {is_complete: isComplete}),
          ...(screenUid !== undefined && {screen_uid: screenUid}),
          page,
        }) as StaticLandingGenreParam;

        const response = await getLanding<
          GenreStaticLandingPageVo,
          StaticLandingGenreParam
        >(StaticLandingType.Genre, queryParam, fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '장르전체 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        return createLayout({
          userInfo: null,
          param: {
            genreStaticLandingPageVo: response.result,
            input: variables.queryInput,
            type: variables.queryInput.type,
          },
        });
      },
      ...options,
    }),

  /**
   * 오리지널 고정랜딩 레이아웃 조회
   *
   * Origin: staticLandingOriginalLayout.resolver.ts
   * ⚠️ userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
   */
  staticLandingOriginalLayout: (
    variables?: StaticLandingOriginalLayoutQueryVariables,
    options?: Omit<
      UseQueryOptions<Layout | null, PagewebError, Layout | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.staticLandingOriginalLayout(variables),
      queryFn: async (): Promise<Layout | null> => {
        if (!variables?.queryInput) {
          return null;
        }
        const {categoryUid, subcategoryUid, sortType, isComplete, page} =
          variables.queryInput;

        const queryParam: StaticLandingOriginalParam = trimUndefinedFromObject({
          category_uid: categoryUid,
          ...(subcategoryUid && {subcategory_uid: Number(subcategoryUid)}),
          ...(sortType && {sort_type: sortType}),
          ...(isComplete && {is_complete: isComplete}),
          page,
        }) as StaticLandingOriginalParam;

        const response = await getLanding<
          OriginalStaticLandingPageVo,
          StaticLandingOriginalParam
        >(StaticLandingType.Original, queryParam, fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '오리지널 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        return createLayout({
          userInfo: null,
          param: {
            originalStaticLandingPageVo: response.result,
            input: variables.queryInput,
          },
        });
      },
      ...options,
    }),

  /**
   * 랭킹 고정랜딩 레이아웃 조회
   *
   * Origin: staticLandingRankingLayout.resolver.ts
   * ⚠️ userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
   */
  staticLandingRankingLayout: (
    variables?: StaticLandingRankingLayoutQueryVariables,
    options?: Omit<
      UseQueryOptions<Layout | null, PagewebError, Layout | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.staticLandingRankingLayout(variables),
      queryFn: async (): Promise<Layout | null> => {
        if (!variables?.queryInput) {
          return null;
        }
        const {categoryUid, subcategoryUid, rankingType, page, screenUid} =
          variables.queryInput;

        const queryParam: StaticLandingRankingParam = trimUndefinedFromObject({
          category_uid: categoryUid,
          ...(subcategoryUid && {subcategory_uid: Number(subcategoryUid)}),
          ...(rankingType && {ranking_type: rankingType}),
          ...(screenUid !== undefined && {screen_uid: screenUid}),
          page,
        }) as StaticLandingRankingParam;

        const response = await getLanding<
          RankingStaticLandingPageVo,
          StaticLandingRankingParam
        >(StaticLandingType.Ranking, queryParam, fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '랭킹 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        return createLayout({
          userInfo: null,
          param: {
            rankingStaticLandingPageVo: response.result,
            input: variables.queryInput,
            type: variables.queryInput.type,
          },
        });
      },
      ...options,
    }),

  /**
   * 단행본 랭킹 테마 고정랜딩 레이아웃 조회
   *
   * API: GET /api/gateway/view/v1/landing/ranking_theme
   */
  staticLandingRankingThemeLayout: (
    variables?: StaticLandingRankingThemeLayoutQueryVariables,
    options?: Omit<
      UseQueryOptions<Layout | null, PagewebError, Layout | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey:
        LANDING_QUERY_KEY_MAP.staticLandingRankingThemeLayout(variables),
      queryFn: async (): Promise<Layout | null> => {
        if (!variables?.queryInput) {
          return null;
        }
        const {screenUid, filterType, sortType, txId, page} =
          variables.queryInput;

        const response = await getLanding<
          RankingThemeStaticLandingPageVo,
          {
            screen_uid?: number;
            filter_type?: string;
            sort_type?: string;
            page?: number;
            tx_id?: string;
          }
        >(
          StaticLandingType.RankingTheme,
          trimUndefinedFromObject({
            screen_uid: typeof screenUid === 'number' ? screenUid : undefined,
            filter_type: filterType,
            sort_type: sortType,
            page,
            tx_id: txId,
          }),
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError(
            '단행본 랭킹 테마 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        return createLayout({
          userInfo: null,
          param: {
            rankingThemeStaticLandingPageVo: response.result,
            input: variables.queryInput,
            type: variables.queryInput.type,
          },
        });
      },
      ...options,
    }),

  /**
   * 오늘NEW 고정랜딩 레이아웃 조회
   *
   * Origin: staticLandingTodayNewLayout.resolver.ts
   * ⚠️ userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
   */
  staticLandingTodayNewLayout: (
    variables?: StaticLandingTodayNewLayoutQueryVariables,
    options?: Omit<
      UseQueryOptions<Layout | null, PagewebError, Layout | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.staticLandingTodayNewLayout(variables),
      queryFn: async (): Promise<Layout | null> => {
        if (!variables?.queryInput) {
          return null;
        }
        const {categoryUid, subcategoryUid, screenUid} = variables.queryInput;

        const queryParam: StaticLandingTodayNewParam = trimUndefinedFromObject({
          category_uid: categoryUid,
          ...(subcategoryUid && {subcategory_uid: Number(subcategoryUid)}),
          ...(screenUid !== undefined && {screen_uid: screenUid}),
        }) as StaticLandingTodayNewParam;

        const response = await getLanding<
          TodayNewStaticLandingPageVo,
          StaticLandingTodayNewParam
        >(StaticLandingType.TodayNew, queryParam, fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '오늘신작 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        return createLayout({
          userInfo: null,
          param: {
            todayNewStaticLandingPageVo: response.result,
            input: variables.queryInput,
            type: variables.queryInput.type,
          },
        });
      },
      ...options,
    }),

  /**
   * 오늘UP 고정랜딩 레이아웃 조회
   *
   * Origin: staticLandingTodayUpLayout.resolver.ts
   * ⚠️ userInfoToken은 서버 사이드 context이므로 클라이언트에서는 null 전달
   * ⚠️ 복잡한 로직: 두 개의 API 호출 (TodayUp + TodayUpInven 조건부)
   */
  staticLandingTodayUpLayout: (
    variables?: StaticLandingTodayUpLayoutQueryVariables,
    options?: Omit<
      UseQueryOptions<Layout | null, PagewebError, Layout | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LANDING_QUERY_KEY_MAP.staticLandingTodayUpLayout(variables),
      queryFn: async (): Promise<Layout | null> => {
        if (!variables?.queryInput) {
          return null;
        }
        const {categoryUid, subcategoryUid, page} = variables.queryInput;

        const queryParam: StaticLandingTodayUpParam = trimUndefinedFromObject({
          category_uid: categoryUid,
          ...(subcategoryUid && {subcategory_uid: Number(subcategoryUid)}),
          page,
        }) as StaticLandingTodayUpParam;

        const response = await getLanding<
          TodayUpStaticLandingPageVo,
          StaticLandingTodayUpParam
        >(StaticLandingType.TodayUp, queryParam, fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError(
            '오늘UP 고정랜딩 데이터를 찾을 수 없습니다.'
          );
        }

        // TodayUpInven API 조건부 호출
        // 조건: '전체' 서브탭 & 첫 페이지인 경우만
        let invenResult;
        if (
          (!subcategoryUid || subcategoryUid === '0') &&
          (page === 0 || page === undefined)
        ) {
          try {
            const invenQueryParam: StaticLandingTodayUpInvenParam =
              trimUndefinedFromObject({
                category_uid: categoryUid,
                ...(subcategoryUid && {
                  subcategory_uid: Number(subcategoryUid),
                }),
              }) as StaticLandingTodayUpInvenParam;

            const invenResponse = await getLanding<
              TodayUpInvenStaticLandingPageVo,
              StaticLandingTodayUpInvenParam
            >(StaticLandingType.TodayUpInven, invenQueryParam, fetcherOptions);

            if (!invenResponse.result) {
              throw createSimpleApiResponseError(
                '오늘UP Inven 데이터를 찾을 수 없습니다.'
              );
            }
            invenResult = invenResponse.result;
          } catch (error) {
            invenResult = undefined;
          }
        }

        return createLayout({
          userInfo: null,
          param: {
            todayUpStaticLandingPageVo: response.result,
            todayUpInvenStaticLandingPageVo: invenResult,
            input: variables.queryInput,
          },
        });
      },
      ...options,
    }),
} as const;
